import { createFileRoute, Link } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Pre, Automate, H3 } from '../components/docs/prose'
import { DoubleEntryDiagram } from '../components/docs/DoubleEntryDiagram'

export const Route = createFileRoute('/docs/double-entry')({ component: DoubleEntry })

const SECTIONS = [
  { id: 'idea', title: 'The idea' },
  { id: 'signs', title: 'Signs' },
  { id: 'enforcement', title: 'How it is enforced' },
  { id: 'currency', title: 'Two currencies, one zero' },
  { id: 'consequences', title: 'What this buys you' },
]

function DoubleEntry() {
  return (
    <DocPage
      path="/docs/double-entry"
      title="Double-entry, explained"
      lede={
        <>
          Double-entry has a reputation for being an accountant&rsquo;s ritual.
          It is not. It is one sentence — money leaving somewhere arrives
          somewhere — plus the discipline to write both halves down every time.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="idea" title="The idea">
        <P>
          Single-entry bookkeeping records what happened to one account:
          &ldquo;Visa, minus $200.&rdquo; That is half a fact. The money went
          somewhere, and the half that says where is missing. Add up a year of
          those halves and you get a number that is plausible but unverifiable.
        </P>
        <P>
          Double-entry records both halves as one thing. In FolioWorth that
          thing is a <strong>journal entry</strong>, and each half is a{' '}
          <strong>ledger line</strong>. An entry has a date, a description, and
          two or more lines. The lines sum to zero.
        </P>

        <DoubleEntryDiagram />

        <P>
          Nothing here is a special case. Salary arriving is the same shape:
          chequing up, <C>Income: Salary</C> down. Paying the Visa is the same
          shape: Visa up (you owe less), chequing down. Once you see that every
          movement is a pair, the whole model is four columns wide.
        </P>
      </Section>

      <Section id="signs" title="Signs">
        <P>
          FolioWorth uses one sign convention everywhere:{' '}
          <strong>positive means value arrived in that account</strong>. That
          is what accountants call a debit. Negative means value left it, a
          credit. You never have to type either word.
        </P>
        <Pre>{`Salary        Chequing            +5000
              Income: Salary      -5000

Groceries     Expense: Groceries   +200
              Visa                 -200      (you owe more)

Pay the card  Visa                 +200      (you owe less)
              Chequing             -200`}</Pre>
        <P>
          The third example is the one worth staring at. Paying a credit card
          makes the card&rsquo;s balance go <em>up</em>, toward zero, because a
          debt is stored as a negative number. Nothing is being spent — the
          money is moving between two things you already had on the books, and
          your net worth does not change.
        </P>
      </Section>

      <Section id="enforcement" title="How it is enforced">
        <P>
          The rule is a Postgres constraint trigger, not application code:
        </P>
        <Pre>{`CREATE CONSTRAINT TRIGGER ledger_lines_balance_check
    AFTER INSERT OR UPDATE OR DELETE ON ledger_lines
    DEFERRABLE INITIALLY DEFERRED
    FOR EACH ROW EXECUTE FUNCTION assert_entry_balanced();`}</Pre>
        <H3>Why deferred</H3>
        <P>
          An entry is written one line at a time. After the first line, the sum
          is not zero — the entry is half built. A normal trigger would fire
          then and reject it. <C>DEFERRABLE INITIALLY DEFERRED</C> holds the
          check until <C>COMMIT</C>, when the entry is complete, so the
          intermediate state is allowed but the committed state never is.
        </P>
        <P>
          The check rejects two things: an entry with fewer than two lines, and
          an entry whose lines do not sum to exactly zero. Both raise a{' '}
          <C>check_violation</C> and abort the whole transaction. There is no
          override, no admin bypass, and no import path that writes around it.
        </P>
        <P>
          Delete every line of an entry and the check passes — zero lines means
          the entry is gone, not unbalanced. That is the only exemption.
        </P>
      </Section>

      <Section id="currency" title="Two currencies, one zero">
        <P>
          A line stores two amounts. <C>amount</C> is in the account&rsquo;s own
          currency; <C>amount_base</C> is in your household&rsquo;s base
          currency. Only <C>amount_base</C> has to sum to zero, because a
          transfer from a CAD chequing account to a USD account cannot balance
          in either currency alone.
        </P>
        <P>
          The rate used is stored on the line as <C>fx_rate</C> rather than
          looked up later. A movement that happened at 1.38 stays at 1.38
          forever; your 2019 history does not silently re-price itself every
          time the dollar moves.
        </P>
      </Section>

      <Section id="consequences" title="What this buys you">
        <DefList
          items={[
            [
              'An exact flow map',
              <>
                Edges on the{' '}
                <Link to="/docs/flow-map" style={{ color: 'var(--accent-text)' }}>money flow map</Link>{' '}
                come from lines that share an entry. There is no heuristic
                pairing a withdrawal with a deposit that looks similar — the
                entry already states they are the same movement.
              </>,
            ],
            [
              'Net worth that reconciles',
              <>
                Assets, investments and debts are the same signed numbers
                added together. There is no separate debt total that can drift
                out of step with the accounts it summarises.
              </>,
            ],
            [
              'No orphan money',
              <>
                Every dollar in the system has a counterparty. If spending
                looks wrong, you can always ask which entry produced it,
                because there is always exactly one.
              </>,
            ],
            [
              'Safe imports',
              <>
                Imported entries carry an <C>external_id</C>, unique per
                household. Re-running the same import writes nothing the second
                time, so a retry after a network failure is harmless.
              </>,
            ],
          ]}
        />
      </Section>

      <Automate>
        You are not expected to type both legs. Bank connections post entries
        with both sides already filled in — the transaction hits the real
        account, and the counterparty leg lands in an Income or Expense
        account chosen from the category. Manual double-entry is the fallback,
        not the workflow. See{' '}
        <Link to="/docs/automating" style={{ color: 'var(--accent-text)' }}>Automating it</Link>.
      </Automate>
    </DocPage>
  )
}
